/**
 * Where everything sits on the player mat, and the path a marker takes when a
 * tray is picked up. Pure, so the ring can be checked without a DOM — if this
 * ran anticlockwise every forecast on the mat would be a lie.
 *
 * The ring is an oval rather than a circle: the mat lies along the bottom of
 * the table, where there is more width than height, and an oval keeps every
 * tray and every Trajan slot clear of its neighbours in that space. Coordinates
 * are the mat's own, from its top-left corner; the table places the mat.
 */
import { packPitch, r2 } from '../layout.js';

export const TRAY_COUNT = 6;

export const MAT_W = 440;
export const MAT_H = 332;
/** Centre of the ring. */
export const CX = 220;
export const CY = 166;
/** Radii of the oval the tray centres sit on. */
export const RING_RX = 120;
export const RING_RY = 70;
/** Radii of the oval the Trajan slots sit on, just outside the trays. */
export const SLOT_RX = 215;
export const SLOT_RY = 134;

export const TRAY_W = 100;
export const TRAY_H = 64;
export const SLOT_W = 56;
export const SLOT_H = 56;
export const MARKER = 15;

export interface Point {
  x: number;
  y: number;
}

/**
 * The point `i` sixths of the way round an oval. Index 0 is at the top and the
 * ring runs clockwise, matching both the mat and the direction markers drop.
 * `i` may be fractional, which is how a token is moved smoothly between trays.
 */
export function ringPoint(i: number, rx: number = RING_RX, ry: number = RING_RY): Point {
  const a = (i * 2 * Math.PI) / TRAY_COUNT;
  return { x: CX + rx * Math.sin(a), y: CY - ry * Math.cos(a) };
}

export const trayCentre = (i: number): Point => ringPoint(i, RING_RX, RING_RY);
export const slotCentre = (i: number): Point => ringPoint(i, SLOT_RX, SLOT_RY);

/** Spacing for the markers sitting in a tray; a full tray packs them closer. */
export const markerPitch = (count: number): number => packPitch(count, TRAY_W - 27, MARKER + 4);

/**
 * Which row of a tray a marker sits in. At rest a tray has one row. While
 * markers are arriving, the ones already there pack into the upper row and the
 * arrivals — never more than two in one tray, because a move sows at most
 * twelve round six trays — sit at full spacing in the lower row, so each one
 * can be picked up on its own however crowded the tray.
 */
export type MarkerRow = 'only' | 'upper' | 'lower';

const ROW_TOP: Record<MarkerRow, number> = { only: 20, upper: 17, lower: 38 };

/** Where the markers sit inside tray `index` when a row of it holds `count` of them. */
export function trayRow(index: number, count: number, row: MarkerRow = 'only') {
  const { x, y } = trayCentre(index);
  const pitch = row === 'lower' ? MARKER + 6 : markerPitch(count);
  const width = count > 0 ? (count - 1) * pitch + MARKER : 0;
  const left = x - TRAY_W / 2 + (TRAY_W - width) / 2;
  const top = y - TRAY_H / 2 + ROW_TOP[row];
  return {
    /** Top-left of the first marker box. */
    left,
    top,
    pitch,
    /** Centre of the `k`th marker. */
    centre: (k: number): Point => ({ x: left + k * pitch + MARKER / 2, y: top + MARKER / 2 }),
  };
}

/**
 * The path from tray `from` clockwise to the tray `steps` further on: one arc
 * of the oval per tray, through the centre of every tray a marker is dropped
 * into, so the route is visible rather than implied. Built a segment at a time
 * because a move of six or twelve returns to its own tray, and a single SVG arc
 * whose endpoints coincide is not drawn at all.
 */
export function ringPath(
  from: number,
  steps: number,
  rx: number = RING_RX,
  ry: number = RING_RY,
): string {
  const start = ringPoint(from, rx, ry);
  let d = `M ${r2(start.x)} ${r2(start.y)}`;
  for (let k = 1; k <= steps; k++) {
    const p = ringPoint(from + k, rx, ry);
    d += ` A ${rx} ${ry} 0 0 1 ${r2(p.x)} ${r2(p.y)}`;
  }
  return d;
}

/**
 * How long the hand takes to travel `steps` trays, in seconds. Proportional to
 * the distance, so a one-tray move is a flick and a six-tray move is a lap, and
 * the speed reads the same either way.
 */
export function flightSeconds(steps: number, perStep = 0.34): number {
  return Math.max(perStep, steps * perStep);
}
